import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import useDebounce from "../../../hooks/custom-hooks/use-debounce";

// Generic, controlled search box, debounced via the shared
// `src/hooks/custom-hooks/use-debounce.js` hook (default 500ms) rather than
// a hand-rolled setTimeout (see the legacy order table for the pattern this
// deliberately avoids repeating).
//
// Prop API:
//   - value: string — the committed value the caller owns (e.g. query
//       state). Used to re-sync this input's local draft when the caller
//       resets it externally (a "Clear Filters" action, for instance) —
//       without this the input would keep showing stale typed text.
//   - onChange: (nextValue: string) => void
//       Called `delay` ms after the user stops typing (not on every
//       keystroke) with the latest raw text. Not called again once the
//       debounced text matches `value`, so clearing externally doesn't
//       bounce back an onChange for the same value.
//   - delay: optional debounce delay in ms (default 500, matching the
//       hook's own default)
//   - placeholder: optional input placeholder text
//
// Internally this keeps a local "draft" string so typing feels instant
// (the input never waits on the debounce or on the caller's re-render) —
// this is the one bit of local state that's inherent to debouncing, not a
// duplicate of caller-owned state.
function SearchInput({ value, onChange, delay, placeholder }) {
    const [draft, setDraft] = useState(value);
    const debouncedDraft = useDebounce(draft, delay);

    // External reset (e.g. Clear Filters sets value back to "") should be
    // reflected immediately, not wait out the debounce.
    useEffect(() => {
        setDraft(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
        if (debouncedDraft !== value) {
            onChange(debouncedDraft);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedDraft]);

    // Presentation matches the legacy order-page search box verbatim: a flat
    // slate fill with no border and 1.1rem text. The two inline declarations
    // are inline in legacy too — `border: "0"` because there's no class that
    // undoes the forms plugin's input border, and the size because 1.1rem is
    // off the Tailwind type scale.
    return (
        <input
            type="search"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={placeholder}
            className="px-6 font-sans w-full bg-slate-100 py-4"
            style={{ fontSize: "1.1rem", border: "0" }}
        />
    );
}

SearchInput.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    delay: PropTypes.number,
    placeholder: PropTypes.string,
};

SearchInput.defaultProps = {
    value: "",
    delay: 500,
    placeholder: "Search...",
};

export default SearchInput;
